import i18next from "i18next";
import getPercentileColor from "../../util/getPercentileColor";
import SRS from "../../../../services/StudentRecordService";
import { getUserData } from 'Services/API';
import { formatScoreByType } from './generateScore';
import { MasteryPacing, MasteryPacingColor } from '../../../../enums/MasteryStatus';
import { getPercentileInfoUsingScaledCutlines } from '../../util/cutscores';
import { ASSESSMENT_TYPE_FILTERS } from '../../../../constants/constants';

const SUBSCORE_SCALES = [
  'decoding',
  'phonAwareScore',
  'highFrequencyWords',
  'backgroundKnowledge',
  'structuresAndReasoning',
  'vocabulary',
  'armScore'
];

export function keyToLabel(key) {
  switch (key) {
    case 'decoding': return 'Decoding';
    case 'phonAwareScore': return 'Phonological Awareness';
    case 'highFrequencyWords': return 'High Frequency Words';
    case 'backgroundKnowledge': return 'Background Knowledge';
    case 'structuresAndReasoning': return 'Structure & Reasoning';
    case 'vocabulary': return 'Vocabulary';
    default: return key;
  }
}

export function buildSubscore(percentile, key, cutlineMap, translationFunction) {
  if (percentile == null) return null;
  
  const cutline = getPercentileInfoUsingScaledCutlines(percentile, cutlineMap[key] || []);
  const t = translationFunction || i18next.getFixedT(null, 'teacher_parent_report');
  
  // If we have valid cutlines, use them; otherwise show no data
  if (cutline && cutline.color) {
    return {
      percentile,
      color: cutline.color,
      statusText: t(cutline.label || ''),
      description: t(`domain_descriptions.${key}`),
      label: keyToLabel(key),
      pacing: cutline.label || null,
      cutline: cutlineMap[key] || []
    };
  } else {
    // No cutlines available - show no data instead of null/empty values
    return {
      percentile,
      color: MasteryPacingColor.NO_DATA,
      statusText: t('no_data'),
      description: t(`domain_descriptions.${key}`),
      label: keyToLabel(key),
      pacing: MasteryPacing.NO_DATA,
      cutline: null
    };
  }
}

export async function generateSubscores(classId, studentId, studentIds, useARM, locale, useLatestARM, startDate, endDate, districtId, schoolId, grade, period, screeningWindow = null, selectedAssessmentTypeFilter = null) {

  const cutlineMap = await fetchAllCutlines(districtId, schoolId, grade, period, locale);

  if (locale === "ES_MX") {
    // For Spanish, use appropriate reading rope data call based on selection type
    let readingRopeParams = { locale, useLatestARM };
    
    if (studentId) {
      // Individual student
      readingRopeParams.studentId = studentId;
    } else if (classId) {
      // Whole class
      readingRopeParams.classId = classId;
    }
    
    const { readingRopeReport } = await SRS.getReadingRopeData(readingRopeParams);
    const subscores = generateSubscoresFromReadingRope(readingRopeReport, locale, cutlineMap);
    return subscores;
  } else {
    // For whole class (studentIds array) vs individual student (studentId)
    const subscores = await generateSubscoresFromStudentRecordStore(studentId, studentIds, locale, startDate, endDate, cutlineMap, period, screeningWindow, selectedAssessmentTypeFilter);
    return subscores;
  }
}

async function fetchAllCutlines(districtId, schoolId, grade, periodId, locale) {
  const cutlineMap = {};

  await Promise.all(SUBSCORE_SCALES.map(async (scale) => {
    const params = {
      districtId,
      schoolId,
      grade,
      scale,
      periodId: [periodId]
    };
    
    // Only add locale for Spanish, as English might not need it
    if (locale === "ES_MX") {
      params.locale = locale;
    }
    
    const cutlines = await SRS.getScaledCutlines(params);
    cutlineMap[scale] = cutlines;
  }));

  return cutlineMap;
}

export async function generateSubscoresFromStudentRecordStore(studentId, studentIds, locale = "EN_US", startDate, endDate, cutlineMap, period = null, screeningWindow = null, selectedAssessmentTypeFilter = null) {
  // Determine which studentIds to use
  const idsToQuery = studentIds && studentIds.length > 0 ? studentIds : (studentId ? [studentId] : []);
  
  if (idsToQuery.length === 0) {
    console.warn('No student IDs provided for assessment data');
    return null;
  }

  // Handle different assessment type filters
  let testType = null;
  
  if (selectedAssessmentTypeFilter === ASSESSMENT_TYPE_FILTERS.BENCHMARK) {
    testType = ASSESSMENT_TYPE_FILTERS.BENCHMARK;
  } else if (selectedAssessmentTypeFilter === ASSESSMENT_TYPE_FILTERS.PROGRESS_MONITORING) {
    testType = null;
  }

  const assessments = await SRS.getLatestAssessments({
    studentIds: idsToQuery,
    dateStart: startDate,
    dateEnd: endDate,
    locale,
    periodId: screeningWindow ? undefined : period,
    screeningWindow: screeningWindow,
    testType: testType
  });

  if (!assessments || assessments.length === 0) {
    console.log('No assessment data found for students');
    return null;
  }

  const t = i18next.getFixedT(null, 'teacher_parent_report');

  // If single student, use existing logic
  if (idsToQuery.length === 1) {
    const latestAssessment = assessments[0];
    if (!latestAssessment?.scores) return null;

    const { scores } = latestAssessment;

    // Helper function that uses the extracted buildSubscore
    const buildSubscoreFromScores = (key, scoreKey) => {
      const percentile = scores[scoreKey];
      return buildSubscore(percentile, key, cutlineMap, t);
    };

    // Extract ARM score and PR
    const armScore = scores.armScore || null;
    const armScorePR = scores.armScorePR || null;
    const armCutline = armScorePR ? getPercentileInfoUsingScaledCutlines(armScorePR, cutlineMap['armScore'] || []) : null;

    const finalArmScore = {
      value: formatScoreByType(armScore, 'ARM'),
      percentile: armScorePR,
      pacing: (armCutline && armCutline.pacing) ? armCutline.pacing : MasteryPacing.NO_DATA,
      color: (armCutline && armCutline.color) ? armCutline.color : MasteryPacingColor.NO_DATA
    };

    return {
      decoding: buildSubscoreFromScores('decoding', 'decodeScore_PR'),
      phonologicalAwareness: buildSubscoreFromScores('phonAwareScore', 'phonAwareScore_PR'),
      highFrequencyWords: buildSubscoreFromScores('highFrequencyWords', 'esriScore_PR'),
      backgroundKnowledge: buildSubscoreFromScores('backgroundKnowledge', 'bkScore_PR'),
      structuresAndReasoning: buildSubscoreFromScores('structuresAndReasoning', 'compScore_PR'),
      vocabulary: buildSubscoreFromScores('vocabulary', 'vocabularyScreenerScore_PR'),
      armScore: finalArmScore,
      // Include ARM cutlines for thread averages to use
      _armCutlines: cutlineMap['armScore'] || []
    };
  }
  
  // Helper function to safely extract numeric values and filter out null/undefined
  const getValidScores = (scoreKey) => {
    return assessments
      .map(assessment => assessment.scores?.[scoreKey])
      .filter(score => score !== null && score !== undefined && !isNaN(score))
      .map(score => Number(score));
  };

  // Helper function to calculate average
  const calculateAverage = (values) => {
    if (values.length === 0) return null;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum / values.length;
  };

  // Calculate averages for each score type
  const avgDecoding = calculateAverage(getValidScores('decodeScore_PR'));
  const avgPhonAware = calculateAverage(getValidScores('phonAwareScore_PR'));
  const avgHighFreq = calculateAverage(getValidScores('esriScore_PR'));
  const avgBackgroundKnowledge = calculateAverage(getValidScores('bkScore_PR'));
  const avgStructuresReasoning = calculateAverage(getValidScores('compScore_PR'));
  const avgVocabulary = calculateAverage(getValidScores('vocabularyScreenerScore_PR'));
  const avgArmScore = calculateAverage(getValidScores('armScore'));
  const avgArmScorePR = calculateAverage(getValidScores('armScorePR'));

  // Helper function to build subscores from averaged percentiles
  const buildSubscoreFromAverage = (key, avgPercentile) => {
    if (avgPercentile === null) return null;
    const flooredPercentile = Math.floor(avgPercentile);
    return buildSubscore(flooredPercentile, key, cutlineMap, t);
  };

  // Build ARM score with cutlines
  const armCutline = avgArmScorePR ? getPercentileInfoUsingScaledCutlines(Math.floor(avgArmScorePR), cutlineMap['armScore'] || []) : null;
  
  const finalArmScore = {
    value: avgArmScore ? formatScoreByType(avgArmScore, 'ARM') : null,
    percentile: avgArmScorePR ? Math.floor(avgArmScorePR) : null,
    pacing: (armCutline && armCutline.pacing) ? armCutline.pacing : MasteryPacing.NO_DATA,
    color: (armCutline && armCutline.color) ? armCutline.color : MasteryPacingColor.NO_DATA
  };

  console.log('Class averages calculated:', {
    decoding: avgDecoding,
    phonAware: avgPhonAware,
    armScore: avgArmScore,
    studentsWithData: assessments.length,
    totalStudents: idsToQuery.length
  });

  return {
    decoding: buildSubscoreFromAverage('decoding', avgDecoding),
    phonologicalAwareness: buildSubscoreFromAverage('phonAwareScore', avgPhonAware), 
    highFrequencyWords: buildSubscoreFromAverage('highFrequencyWords', avgHighFreq),
    backgroundKnowledge: buildSubscoreFromAverage('backgroundKnowledge', avgBackgroundKnowledge),
    structuresAndReasoning: buildSubscoreFromAverage('structuresAndReasoning', avgStructuresReasoning),
    vocabulary: buildSubscoreFromAverage('vocabulary', avgVocabulary),
    armScore: finalArmScore,
    // Include ARM cutlines for thread averages to use
    _armCutlines: cutlineMap['armScore'] || []
  };
}

export function generateSubscoresFromReadingRope(readingRope, locale, cutlineMap) {
  if (!readingRope || !readingRope.threads?.length) return null;
  const domains = [].concat(...readingRope.threads.map(({ domains }) => domains || []));

  const result = {
    decoding: transformDomainToSubScore(domains.find(d => d.label === 'Decoding'), 'decoding', cutlineMap),
    phonologicalAwareness: transformDomainToSubScore(domains.find(d => d.label === 'Phonological Awareness'), 'phonologicalAwareness', cutlineMap),
    highFrequencyWords: transformDomainToSubScore(domains.find(d => d.label === 'Sight Recognition'), 'highFrequencyWords', cutlineMap),
    backgroundKnowledge: transformDomainToSubScore(domains.find(d => d.label === 'Background Knowledge'), 'backgroundKnowledge', cutlineMap),
    structuresAndReasoning: transformDomainToSubScore(domains.find(d => d.label === 'Structure & Reasoning'), 'structuresAndReasoning', cutlineMap),
    vocabulary: transformDomainToSubScore(domains.find(d => d.label === 'Vocabulary'), 'vocabulary', cutlineMap),
    // Include ARM cutlines for thread averages to use
    _armCutlines: cutlineMap['armScore'] || []
  };

  return result;
}

function transformDomainToSubScore(domain, key, cutlineMap) {
  if (!domain || !domain.mastery) return null;
  const { mastery } = domain;
  const t = i18next.getFixedT(null, 'teacher_parent_report');
  
  try {
    // Try to get cutline from API first
    const cutline = getPercentileInfoUsingScaledCutlines(mastery.value, cutlineMap[key] || []);
    
    // If we have cutlines from API, use them
    if (cutline && cutline.color) {
      return {
        percentile: mastery.value,
        color: cutline.color,
        statusText: t(cutline.pacing || ''),
        description: t(`domain_descriptions.${domain.label}`),
        label: domain.label,
        pacing: cutline.pacing || null,
        cutline: cutlineMap[key] || []
      };
    } else {
      // No cutlines available - show no data instead of incorrect data
      return {
        percentile: mastery.value,
        color: MasteryPacingColor.NO_DATA,
        statusText: t('no_data'),
        description: t(`domain_descriptions.${domain.label}`),
        label: domain.label,
        pacing: MasteryPacing.NO_DATA,
        cutline: null
      };
    }
  } catch (error) {
    console.error('Error getting cutlines for domain', key, error);
    // On error - show no data instead of incorrect data
    return {
      percentile: mastery.value,
      color: MasteryPacingColor.NO_DATA,
      statusText: t('no_data'),
      description: t(`domain_descriptions.${domain.label}`),
      label: domain.label,
      pacing: MasteryPacing.NO_DATA,
      cutline: null
    };
  }
}